import React from 'react';
import { motion } from 'framer-motion';
import { getClassificationForElo, getClassificationColor } from '../../utils/classificationUtils';
import { getNextClassificationThreshold, getCurrentClassificationThreshold } from '../../utils/rankingUtils';
import { EloData } from '../../types/api.types';

interface PlayerClassificationCardProps {
  playerData: EloData;
  isLikelyFemale: boolean;
  currentLang: 'en' | 'fr' | 'de' | 'it';
  t: {
    classificationProgress: string;
    pointsTo: string;
  };
}

/**
 * PlayerClassificationCard Component
 * Displays classification progress bar with current and next class badges
 */
const PlayerClassificationCard: React.FC<PlayerClassificationCardProps> = ({
  playerData,
  isLikelyFemale,
  currentLang,
  t
}) => {
  const currentClass = getClassificationForElo(playerData.current_elo, isLikelyFemale);
  const currentColors = getClassificationColor(currentClass);
  const nextClass = getNextClassificationThreshold(currentClass, isLikelyFemale);

  if (!nextClass) return null;

  const currentThreshold = getCurrentClassificationThreshold(currentClass, isLikelyFemale);
  const pointsToNext = nextClass.threshold - playerData.current_elo;
  const totalRange = nextClass.threshold - currentThreshold;
  const progressPercent = ((playerData.current_elo - currentThreshold) / totalRange) * 100;
  const nextColors = getClassificationColor(nextClass.class);

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-2xl p-6 md:p-8 shadow-lg transition-colors duration-300"
      initial={{ opacity: 0, scale: 0.9, y: 0 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4, type: "spring", stiffness: 100 }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400 transition-colors duration-300">
          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center transition-colors duration-300">
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <span className="text-sm uppercase tracking-wider">{t.classificationProgress}</span>
        </div>
        <div className="text-right">
          <div className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">{Math.round(pointsToNext)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">{t.pointsTo} {nextClass.class}</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="flex items-center gap-3 md:gap-4">
        {/* Current Class Badge */}
        <motion.div
          className="px-4 py-3 md:px-5 md:py-4 rounded-xl font-bold flex flex-col items-center justify-center min-w-[70px] md:min-w-[80px] flex-shrink-0"
          style={{
            backgroundColor: currentColors.bg,
            color: currentColors.text
          }}
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}>
          <div className="text-xl md:text-2xl">{currentClass}</div>
          <div className="text-xs opacity-80">{currentThreshold}</div>
        </motion.div>

        {/* Progress Bar Container */}
        <div className="flex-1 min-w-0">
          <div className="relative h-4 md:h-5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden transition-colors duration-300">
            <motion.div
              className="h-full rounded-full transition-colors duration-300"
              style={{
                background: `linear-gradient(to right, ${currentColors.bg}, ${nextColors.bg})`
              }}
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
            />
            {/* Average ELO Marker */}
            {playerData.ranking_info?.avg_elo && (() => {
              const avgPosition = ((playerData.ranking_info.avg_elo - currentThreshold) / (nextClass.threshold - currentThreshold)) * 100;
              if (avgPosition >= 0 && avgPosition <= 100) {
                return (
                  <motion.div
                    className="absolute top-0 bottom-0 w-0.5 bg-yellow-300/80 shadow-lg"
                    style={{ left: `${avgPosition}%` }}
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={{ opacity: 1, scaleY: 1 }}
                    transition={{ duration: 0.5, delay: 1.2 }}
                  >
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-yellow-300 whitespace-nowrap font-medium">
                      AVG
                    </div>
                  </motion.div>
                );
              }
              return null;
            })()}
          </div>
          {/* Info below progress bar */}
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <span>{Math.round(progressPercent)}%</span>
            <span>{playerData.current_elo}</span>
            {playerData.ranking_info?.avg_elo && (
              <span className="text-yellow-600 dark:text-yellow-500 transition-colors duration-300">Ø {playerData.ranking_info.avg_elo}</span>
            )}
          </div>
        </div>

        {/* Next Class Badge */}
        <motion.div
          className="px-4 py-3 md:px-5 md:py-4 rounded-xl font-bold flex flex-col items-center justify-center min-w-[70px] md:min-w-[80px] flex-shrink-0"
          style={{
            backgroundColor: nextColors.bg,
            color: nextColors.text
          }}
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}>
          <div className="text-xl md:text-2xl">{nextClass.class}</div>
          <div className="text-xs opacity-80">{nextClass.threshold}</div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default PlayerClassificationCard;
